import { Link as RouterLink } from "react-router";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getOrderList } from "@/api/order/getOrderList";
import { toTitleCase } from "@/app/text.utils";
import { DetailDrawer, useDetailStackDrawer, useDetailStackId } from "@/components/detail-stack";
import { DETAIL_KIND } from "@/features/detail/detailKind";
import type { DetailKind } from "@/features/detail/detailKind";

/**
 * One customer's details: a `customers/<id>` level of the detail stack, so it can sit over any
 * page or drawer; each of the customer's orders opens on top. Name and orders come from the
 * loaded order list (newest page) until there is a GET /customers/{customerId}.
 */
export const CustomerDetail = () => {
  const level = useDetailStackDrawer<DetailKind>();
  const customerId = useDetailStackId();
  const query = getOrderList.useQuery();
  const orders = (query.data ?? []).filter((order) => order.customerOrder?.customerId === customerId);
  const rawName = orders[0]?.customerOrder?.customerName;
  let customerName = "Customer";
  if (rawName) customerName = toTitleCase(rawName);

  const title = (
    <Typography
      sx={{ fontWeight: 600 }}
      variant="h6"
    >
      {customerName}
    </Typography>
  );

  return (
    <DetailDrawer title={title}>
      <Typography
        sx={{ mb: 1 }}
        variant="subtitle2"
      >
        Orders
      </Typography>
      {orders.length === 0 && (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          No orders for this customer in the loaded list.
        </Typography>
      )}
      <Stack spacing={0.5}>
        {orders.map((order) => (
          <Link
            component={RouterLink}
            key={order.id}
            to={level.toChild(DETAIL_KIND.orders, order.id)}
            underline="hover"
            variant="body2"
          >
            {order.orderNumber || order.id}
          </Link>
        ))}
      </Stack>
    </DetailDrawer>
  );
};
